/**
 * @file Map-marker-storage-manager.tsx
 * @module MapMarkerStorageManager
 * @description Marķieru ģeometriju lokālās krātuves (localStorage) pārvaldnieks.
 *              Sinhronizē OpenLayers VectorSource objektu ģeometrijas ar localStorage,
 *              saglabājot WKT formātā. Nodrošina starpciļņu (cross-tab) sinhronizāciju
 *              caur storage notikumiem — izmanto SIPR sistēmas priekšpuses serviss,
 *              lai uzturētu marķieru stāvokli starp logiem.
 */
import { useEffect } from "react";
import * as ol from "ol/util";
import VectorSource from "ol/source/Vector";
import WKT from "ol/format/WKT";

let UPDATE = true;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export const setUpdate = () => {
	UPDATE = true;
};

interface Storage {
	[key: string]: {
		geom: string;
		uid: string;
		properties: any;
	}[];
}

let list1: any;
let list2: any;
let list3: any;
let storage: Storage;

export const MapMarkerStorageManager = ({ source, objectId }: { source: VectorSource; objectId: string }) => {
	useEffect(() => {
		updateStorage();
	}, [objectId]);

	useEffect(() => {
		list1 = source.on("changefeature", () => updateStorage());
		list2 = source.on("addfeature", () => updateStorage());
		list3 = source.on("removefeature", () => updateStorage());
		return () => {
			[list1, list2, list3].forEach((l) => source.un(l.type, l.listener));
		};
	}, [objectId]);

	const updateStorage = () => {
		if (UPDATE) {
			const items = source.getFeatures().map((f) => {
				const props = f.getProperties();
				delete props.geometry;
				return {
					geom: new WKT().writeGeometry(f.getGeometry()!),
					uid: ol.getUid(f),
					properties: props,
				};
			});
			storage = { [objectId || "new"]: items };
			localStorage.setItem("lvc-geom", JSON.stringify(storage));
		}
	};

	useEffect(() => {
		window.addEventListener("storage", handleStorageEvent);
		return () => {
			window.removeEventListener("storage", handleStorageEvent);
		};
	}, [objectId]);

	const handleStorageEvent = () => {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => onStorageUpdate(), 25);
	};

	const onStorageUpdate = () => {
		UPDATE = false;
		setTimeout(() => {
			UPDATE = true;
		}, 25);

		const string = localStorage.getItem("lvc-geom");
		if (string && string !== JSON.stringify(storage)) {
			const parsed = JSON.parse(string) as Storage;
			const features = parsed[objectId || "new"]?.map((i) => {
				const f = new WKT().readFeature(i.geom);
				const props = i.properties;
				delete props.geometry;
				f.setProperties(i.properties);
				f.setId(i.uid);
				return f;
			});
			if (features) {
				storage = parsed;
				source.clear();
				source.addFeatures(features);
			}
		}
	};

	return <></>;
};
